import { Box, Card, CardBody, Flex, Heading, HStack, Icon, LinkBox, LinkOverlay, Stack, Text, Wrap, WrapItem } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiCalendar, FiClock, FiFileText, FiHash, FiTag } from "react-icons/fi";
import { Link as RouterLink } from "react-router-dom";
import {
  CompetitionStageDto,
  PortalLifecycleStatus,
  type PortalSourcingEventSummaryDto,
} from "../../../../autogen/bff-api";
import { momentLocaleFor } from "../../../../common/formatting/i18nFormatters";
import { urls } from "../../../../urls";
import { PortalLifecycleTag } from "./PortalLifecycleTag";
import {
  formatCpv,
  formatEstimatedValue,
  formatShortDate,
  getBuyerInitials,
  getDaysLeftMeta,
  getProcedureLabel,
} from "../utils/displayFormatters";

export const PortalSourcingEventCard = ({ event }: { event: PortalSourcingEventSummaryDto }) => {
  const { t, i18n } = useTranslation();
  const locale = momentLocaleFor(i18n.language);
  // During the qualification window the summary deadline is the (passed) request deadline —
  // counting down to it would read as an expired competition, so the meta is hidden.
  const inQualificationWindow = event.competitionStage === CompetitionStageDto.Qualification;
  const daysLeftMeta = inQualificationWindow ? null : getDaysLeftMeta(event.bidSubmissionDeadline, t);

  return (
    <LinkBox>
      <Card
        variant="outline"
        rounded="xl"
        boxShadow="sm"
        bg="smBackground"
        borderColor="smBorder"
        transition="all 0.15s ease"
        cursor="pointer"
        _hover={{ borderColor: "smPrimarySoft", boxShadow: "md" }}
      >
        <CardBody px={{ base: "4", md: "6" }} py={{ base: "4", md: "6" }}>
        <Stack spacing="4">
          <HStack spacing="2">
            <Flex
              width="20px"
              height="20px"
              rounded="md"
              bg="smBackgroundSecondary"
              color="smMuted"
              fontSize="10px"
              fontWeight="bold"
              align="center"
              justify="center"
            >
              {getBuyerInitials(event.buyerName)}
            </Flex>
            <Text color="smMuted" fontSize="sm" fontWeight="semibold">
              {event.buyerName}
            </Text>
          </HStack>

          <Stack spacing="2">
            <Heading as="h2" size="md">
              <LinkOverlay as={RouterLink} to={urls.portal.view.go(event.id)}>
                {event.title}
              </LinkOverlay>
            </Heading>

            <HStack spacing="3" flexWrap="wrap">
              <PortalLifecycleTag
                status={event.lifecycle.status}
                stage={event.competitionStage}
                size="sm"
              />
              {event.lifecycle.status === PortalLifecycleStatus.Open && daysLeftMeta != null && (
                <HStack spacing="1.5" color={daysLeftMeta.color}>
                  <Icon as={FiClock} boxSize="3.5" color={daysLeftMeta.iconColor} />
                  <Text fontSize="sm">{daysLeftMeta.label}</Text>
                </HStack>
              )}
            </HStack>
          </Stack>

          {event.cpvCodes && event.cpvCodes.length > 0 && (
            <Wrap spacing="2">
              {event.cpvCodes.map((cpv) => (
                <WrapItem key={cpv.code}>
                  <Box bg="smPrimarySofter" color="smPrimary" px="2" py="1" rounded="md" fontSize="xs" fontWeight="medium">
                    {formatCpv(cpv, i18n.language)}
                  </Box>
                </WrapItem>
              ))}
            </Wrap>
          )}

          <Wrap spacingX={{ base: "3", md: "5" }} spacingY="2" fontSize="sm">
            {event.procedureType && (
              <WrapItem>
                <HStack spacing="1.5">
                  <Icon as={FiTag} boxSize="3.5" color="smMuted" />
                  <Text color="smMuted">{getProcedureLabel(event.procedureType, t)}</Text>
                </HStack>
              </WrapItem>
            )}

            {event.estimatedValue?.value != null && (
              <WrapItem>
                <HStack spacing="1.5">
                  <Icon as={FiFileText} boxSize="3.5" color="smMuted" />
                  <Text color="smMuted">{formatEstimatedValue(event.estimatedValue.value, event.estimatedValue.currency)}</Text>
                </HStack>
              </WrapItem>
            )}

            {!inQualificationWindow && (
              <WrapItem>
                <HStack spacing="1.5">
                  <Icon as={FiCalendar} boxSize="3.5" color="smMuted" />
                  <Text color="smMuted">
                    {t("Deadline")}: {formatShortDate(event.bidSubmissionDeadline, locale)}
                  </Text>
                </HStack>
              </WrapItem>
            )}
          </Wrap>

          <Wrap spacingX="4" spacingY="1" color="smTextDisabled" fontSize="xs">
            {event.referenceId && (
              <WrapItem>
                <HStack spacing="1.5">
                  <Icon as={FiHash} boxSize="3.25" color="smMuted" />
                  <Text color="smTextDisabled" fontWeight="normal">{event.referenceId}</Text>
                </HStack>
              </WrapItem>
            )}

            {event.publishedAt && (
              <WrapItem>
                <Text color="smTextDisabled" fontWeight="normal">
                  {t("Published")}: {formatShortDate(event.publishedAt, locale)}
                </Text>
              </WrapItem>
            )}
          </Wrap>
          </Stack>
        </CardBody>
      </Card>
    </LinkBox>
  );
};
